import React, { useEffect, useState } from 'react'
import { fetchAuth } from '../../utils/api'

export default function AdminSuscripcion() {
  const [estado, setEstado] = useState(null)
  const [pagos, setPagos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState(null)

  const cargar = async () => {
    try {
      setLoading(true)
      setError(null)
      const est = await fetchAuth('/api/admin/suscripcion/')
      const pg = await fetchAuth('/api/admin/suscripcion/pagos/')
      setEstado(est.suscripcion || null)
      setPagos(pg.pagos || [])
    } catch (e) {
      setError(e.message || 'Error al cargar suscripción')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { cargar() }, [])
  useEffect(() => {
    try {
      const qp = new URLSearchParams(window.location.search)
      const st = qp.get('status')
      if (st) cargar()
    } catch {}
  }, [])

  const pagarAhora = async () => {
    try {
      setCheckoutLoading(true)
      setCheckoutError(null)
      const r = await fetchAuth('/api/admin/suscripcion/checkout/', { method: 'POST' })
      const url = r.init_point
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    } catch (e) {
      setCheckoutError(e.message || 'No se pudo iniciar el pago')
    } finally {
      setCheckoutLoading(false)
    }
  }


  const fmt = (s) => {
    if (!s) return '—'
    try {
      const d = new Date(String(s))
      return d.toISOString().slice(0,10)
    } catch {
      return String(s)
    }
  }

  const daysLeft = () => {
    if (!estado || !estado.fecha_proximo_pago) return null
    try {
      const target = new Date(String(estado.fecha_proximo_pago)).getTime()
      const now = Date.now()
      const diff = Math.max(0, Math.floor((target - now) / (1000*60*60*24)))
      return diff
    } catch {
      return null
    }
  }

  const estasAlDia = () => {
    const st = String(estado?.estado || '').toUpperCase()
    const active = st === 'ACTIVA'
    const approved = pagos.some(p => String(p?.estado_pago || '').toLowerCase() === 'approved')
    const remaining = daysLeft()
    return active && (approved || (remaining == null || remaining > 0))
  }

  if (loading) {
    return (
      <div className="flex-1 p-6 lg:p-10 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-lg overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 p-6 lg:p-10 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-lg overflow-y-auto">
      <header className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Suscripción</h1>
        <div className="mt-2 flex items-center gap-3">
          <p className="text-sm text-slate-600">Gestiona tu plan anual y pagos</p>
          <button onClick={cargar} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold shadow hover:shadow-lg active:scale-95">Actualizar</button>
        </div>
      </header>

      {(!estado || estado?.estado === 'SIN_SUSCRIPCION') && (
        <div className="mb-8 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-100 p-6 text-amber-900 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">No tienes una suscripción activa</div>
              <div className="text-xs text-amber-700 mt-1">Activa tu plan anual para habilitar todas las funciones</div>
            </div>
            <button onClick={pagarAhora} disabled={checkoutLoading} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 text-white px-4 py-2 text-sm font-semibold shadow hover:shadow-lg active:scale-95">
              {checkoutLoading ? 'Creando preferencia…' : 'Pagar ahora'}
            </button>
          </div>
        </div>
      )}

      <section className="grid gap-6 lg:grid-cols-3 mb-10">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Estado de suscripción</h3>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${estado?.estado==='ACTIVA' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : estado?.estado==='SIN_SUSCRIPCION' ? 'bg-slate-100 text-slate-700 border-slate-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{estado?.estado || '—'}</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500">Plan</div>
              <div className="text-lg font-bold text-slate-900">{estado?.plan_nombre || '—'}</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500">Monto anual</div>
              <div className="text-lg font-bold text-slate-900">{estado?.monto_anual} {estado?.moneda}</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500">Próximo pago</div>
              <div className="text-lg font-bold text-slate-900">{fmt(estado?.fecha_proximo_pago)}</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500">Fin del período</div>
              <div className="text-lg font-bold text-slate-900">{fmt(estado?.periodo_fin)}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-slate-600">{daysLeft()!==null ? `Quedan ${daysLeft()} días para el próximo pago` : '—'}</div>
            {estasAlDia() ? (
              <div className="inline-flex items-center gap-2 rounded-lg bg-emerald-100 text-emerald-700 px-4 py-2 text-sm font-semibold border border-emerald-200">
                Estás al día
              </div>
            ) : (
              <button onClick={pagarAhora} disabled={checkoutLoading || estasAlDia()} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 text-white px-4 py-2 text-sm font-semibold shadow hover:shadow-lg active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed">
                {checkoutLoading ? 'Creando preferencia…' : 'Pagar ahora'}
              </button>
            )}
          </div>
          {checkoutError && (
            <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{checkoutError}</div>
          )}
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
          <div className="text-sm font-semibold text-slate-900 mb-2">Resumen</div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Días transcurridos</span>
              <span className="text-sm font-bold text-slate-900">{estado?.dias_transcurridos ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Días restantes</span>
              <span className="text-sm font-bold text-slate-900">{estado?.dias_restantes ?? '—'}</span>
            </div>
            {estado?.estado === 'SIN_SUSCRIPCION' && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">Completa tu pago para activar tu período anual</div>
            )}
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Historial de pagos</h3>
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600">ID Pago</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600">Monto</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600">Moneda</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {pagos.length === 0 ? (
                <tr>
                  <td className="px-4 py-4 text-slate-500" colSpan={5}>Sin pagos registrados</td>
                </tr>
              ) : pagos.map(p => (
                <tr key={p.id_pago} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-900">{p.mp_payment_id || p.preference_id || p.id_pago}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${String(p.estado_pago).toLowerCase()==='approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : String(p.estado_pago).toLowerCase()==='pending' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>{p.estado_pago || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-900">{p.monto ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-900">{p.moneda ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-900">{fmt(p.fecha_pagado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {error && (
        <div className="mt-6 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
    </div>
  )
}
